// --- Common types ---

export interface IdNameEntry<T = string> {
  id: T;
  name: string;
}

// --- Start response ---

export type MachineSeries = IdNameEntry;
export interface FoulingFactorVal {
  id: number;
  val: number;
}
export interface AccopRegion {
  id: string;
  englishName: string;
  chineseName: string;
}

export interface SoftwareData {
  version: string;
}

export interface StandardCondition {
  id: string | null;
  descKey: string;
  evapInletType: string;
  evapInletVal: number;
  evapOutletVal: number;
  condInletVal: number;
  condOutletType: string;
  condOutletVal: number;
  evapVarFlowPct: number;
  condVarFlowPct: number;
  evapFFId: number;
  condFFId: number;
}

export interface FrontEndLimitRange {
  min?: number;
  max?: number;
}

export interface FrontEndLimitEntry {
  default: FrontEndLimitRange;
  [seriesId: string]: FrontEndLimitRange;
}

export interface FrontEndLimit {
  [key: string]: FrontEndLimitEntry;
}

export interface StartResponse {
  machineSeriesList: MachineSeries[];
  foulingFactorValList: FoulingFactorVal[];
  softwareData: SoftwareData;
  standardConditionList: StandardCondition[];
  accopRegionList: AccopRegion[];
  frontEndLimit: FrontEndLimit;
}

// --- Compressor Series response ---

export type CompSeries = IdNameEntry<string | null>;

export interface PowerSupply {
  id: number;
  desc: string;
  code: string;
}

export interface Refrigerant {
  name: string;
  code: string;
}

export interface CsResponse {
  compSeries: CompSeries;
  powerSupplySet: PowerSupply[];
  circuitNumSet: number[];
  refSet: Refrigerant[];
  hasLowLift: boolean;
}

// --- Combinations request/response ---

export interface CondOutlet {
  condOutletType: string;
  condOutletVal: number;
  definiteTemp: boolean;
}

export interface CombRequest {
  projectName?: string;
  projectAddress?: string;
  machineSeriesId: string;
  compSeriesList: string[];
  lowLift?: boolean;
  powerSupplyId: number;
  ref: string;
  coolingCapacity: number;
  evapInletType?: string;
  evapInletTemp: number;
  evapOutletTemp: number;
  evapFlowPerKW?: number;
  condInletTemp: number;
  condOutlet: CondOutlet;
  evapVarFlowPct?: number | null;
  condVarFlowPct?: number | null;
  evapFFId?: number;
  condFFId?: number;
  circuitNum?: number;
  cop?: number | null;
  evapPD?: number | null;
  condPD?: number | null;
  selectionMode?: string;
  useEcon?: boolean;
  standardConditionDesc?: string;
  accopRegionId?: string;
}

export interface HeatExchangerSelection {
  id: string;
  heatExchType: string;
  tubeDiaCode: string;
  tubeLen: number;
  pipeNumType: string;
  pipeDiaType: string;
}

export interface CompModelPerfData {
  compModelId: string;
  cop: number;
}

export interface CombResponse {
  evapSelection: HeatExchangerSelection;
  condSelection: HeatExchangerSelection;
  compModelPerfData: CompModelPerfData;
  specialMachineId: string;
  specialMachineDisplayName: string;
}

// --- Field Mappings response ---

export type UomSystem = 'SI' | 'IP';

export interface FreestFieldMappingDto {
  id: number;
  section: string;
  uomGroupId: number | null;
  resourceId: number;
  fixedUomSystem: string;
  siFormatting: string;
  impFormatting: string;
  forcedUom: string;
  printColor: string;
  responseField: string;
  symbol: string;
  conversionParamA: number;
  conversionParamB: number;
  conversionParamC: number;
}

export type FreestFieldMappingMap = Record<string, Record<string, FreestFieldMappingDto>>;

// --- Datasheet PDF request/response ---

export type OptionalSection = 'PART_LOAD_DATA' | 'IPLV_GB' | 'NPLV_SI' | 'ACCOP';

export const ALL_OPTIONAL_SECTIONS: OptionalSection[] = ['PART_LOAD_DATA', 'IPLV_GB', 'NPLV_SI', 'ACCOP'];

export interface SheetRequest {
  evapId: string;
  condId: string;
  compModelId: string;
  specialMachineId: string;
  specialMachineDisplayName?: string;
  loadPctList: number[];
  userName?: string;
  userDataInput: CombRequest;
}

export interface FreestDatasheetPdfRequest {
  sheetRequest: SheetRequest;
  optionalSections?: OptionalSection[] | null;
  uomSystem: UomSystem;
}

export interface ResponseDatasheet {
  url: string;
}

export interface DatasheetSectionsAvailability {
  PART_LOAD_DATA: boolean;
  IPLV: boolean;
  ACCOP: boolean;
}

// --- Compute request/response ---

export interface ComputationItem {
  field: string;
  inputs: Record<string, number>;
}

export interface ComputeRequest {
  uomSystem: UomSystem;
  computations: ComputationItem[];
}

export interface ComputeResponse {
  results: Record<string, number>;
}

// --- Configuration request/response ---

export interface ConfigurationRequest {
  evapId: string;
  condId: string;
  compModelId: string;
  specialMachineId: string | null;
  specialMachineDisplayName: string | null;
  userName?: string;
  userDataInput: CombRequest;
  uomSystem: UomSystem;
}

export interface ConfigurationResponse {
  uuid: string;
}
